/**
 * PaymentConfirmModal — l'étape qui s'intercale entre « Choisir » et Stripe.
 *
 * POURQUOI LA CASE VI.53 N'EST PLUS SUR LES CARTES
 * -----------------------------------------------
 * La case de rétractation (art. VI.53 CDE) doit être cochée AVANT le
 * paiement — pas nécessairement avant d'avoir choisi. Posée sur les quatre
 * cartes de /plans, elle répétait trois fois le même paragraphe juridique
 * dans une vitrine, et transformait la comparaison des offres en formulaire.
 * Elle vit désormais ici, au moment où le client engage réellement son
 * argent : un récapitulatif de ce qu'il va payer, la case, et un bouton
 * qui reste mort tant qu'elle n'est pas cochée.
 *
 * Le CONTRAT AVEC LE BACKEND est inchangé : la session Stripe n'est créée
 * qu'avec `consent_execution: true`, et le serveur refuse sans (400
 * consent_execution_required, cf. backend/services/consent_execution.py).
 * Seul le moment où le client coche a bougé.
 *
 * Cette modale est GÉNÉRIQUE : elle affiche ce qu'on lui donne (nom de
 * l'offre, montant, précisions de cycle) et ne connaît ni les plans ni les
 * prix. Les parcours qui possédaient DÉJÀ une confirmation intermédiaire
 * avec la case dedans — dépassements à l'unité (QuotaModal), envoi de
 * lettre et supplément recommandé (SendLetterOptions), lettre signée
 * (LetterConsent), lettre à l'unité 19 € (PlansV2) — gardent la
 * leur : empiler deux modales pour un seul achat serait pire que le mal.
 */
import React, { useEffect, useState } from 'react';
import ConsentExecutionCheckbox from './ConsentExecutionCheckbox';
import PromoCodeField, { formatCents } from './PromoCodeField';

// Copy ×3. Volontairement hors des dictionnaires de page : cette modale
// sert plusieurs parcours, et son texte ne doit pas dépendre du JSON de
// l'un d'eux. Le texte de la case, lui, reste dans
// ConsentExecutionCheckbox — il est juridique, pas traduisible ici.
const COPY = {
  fr: {
    eyebrow: 'Avant de payer',
    recap: 'Ta commande',
    cancel: 'Annuler',
    pay: 'Continuer vers le paiement',
    busy: 'Redirection…',
    promoThen: 'puis {P} aux échéances suivantes',
  },
  nl: {
    eyebrow: 'Voor je betaalt',
    recap: 'Je bestelling',
    cancel: 'Annuleren',
    pay: 'Door naar de betaling',
    busy: 'Doorsturen…',
    promoThen: 'daarna {P} bij de volgende vervaldagen',
  },
  en: {
    eyebrow: 'Before you pay',
    recap: 'Your order',
    cancel: 'Cancel',
    pay: 'Continue to payment',
    busy: 'Redirecting…',
    promoThen: 'then {P} on following renewals',
  },
};

export default function PaymentConfirmModal({
  open,
  onCancel,
  onConfirm,
  language = 'fr',
  busy = false,
  // Ce qui est acheté : un nom d'offre, un montant, son suffixe éventuel
  // (« /mois », « /an ») et des précisions (cycle, mensualité équivalente,
  // « engagement 12 mois »). Rien n'est calculé ici.
  offerName,
  amount,
  amountSuffix = null,
  details = [],
  error = null,
  testId = 'payment-confirm',
  // ── Code promo (optionnel) ──────────────────────────────────────────
  // Absent par défaut : les parcours qui ne vendent pas d'offre
  // éligible (ou qui n'ont pas de plan à nommer) n'affichent pas de
  // champ dont le serveur refuserait tous les codes.
  promoPlan = null,
  promoBillingCycle = 'monthly',
  // Code déjà saisi plus tôt dans le parcours — celui du formulaire
  // d'inscription. Il est POSÉ dans le champ et revalidé contre cette
  // offre, jamais redemandé. Il n'accorde rien : c'est du texte, que le
  // serveur vérifie au montage puis une nouvelle fois au checkout.
  promoInitialCode = '',
  // Le code appliqué remonte à l'appelant : c'est LUI qui l'envoie au
  // checkout. La modale ne parle pas à Stripe.
  onPromoChange = null,
}) {
  const [consent, setConsent] = useState(false);
  const [promo, setPromo] = useState(null);
  const t = COPY[String(language || 'fr').toLowerCase()] || COPY.fr;

  // Chaque ouverture repart d'une case DÉCOCHÉE — sinon un client qui
  // annule puis rouvre retrouve son consentement précédent, c'est-à-dire
  // une case pré-cochée déguisée. Même règle que LetterConsent.
  // Le code promo repart lui aussi à zéro : un client qui ferme puis
  // rouvre ne doit pas retrouver une remise que le champ n'affiche plus.
  useEffect(() => {
    if (!open) return;
    setConsent(false);
    setPromo(null);
    if (onPromoChange) onPromoChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Échap ferme, comme le clic sur le fond. Un écran qui prend l'argent
  // doit se quitter aussi facilement qu'il s'ouvre.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const blocked = busy || !consent;

  const promoPrice = promo && typeof promo.final_amount === 'number'
    ? formatCents(promo.final_amount, promo.currency)
    : null;
  // La remise porte sur la PREMIÈRE échéance (les coupons créés depuis
  // l'admin sont tous `duration: once`). Un abonné doit lire tout de
  // suite ce qu'il paiera ensuite — c'est la moitié de l'information.
  const shownDetails = (promoPrice !== null && amountSuffix)
    ? [...details, t.promoThen.replace('{P}', `${amount}${amountSuffix}`)]
    : details;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid={testId}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1300, padding: 20,
        background: 'rgba(10,10,15,.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: '#ffffff', borderRadius: 18, border: '1px solid #e2e0db',
          width: '100%', maxWidth: 440,
          maxHeight: 'calc(100dvh - 40px)', overflowY: 'auto',
          padding: '26px 26px 22px',
          boxShadow: '0 30px 60px -20px rgba(15,23,42,.35)',
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 800, color: '#1a56db', letterSpacing: 1, marginBottom: 12 }}>
          {t.eyebrow.toUpperCase()}
        </div>

        {/* Récapitulatif — nom de l'offre à gauche, montant à droite.
            C'est la dernière chose lue avant l'engagement : elle dit ce
            qui est acheté et combien, pas ce que ça contient (la carte
            vient de le dire). */}
        <div
          data-testid={`${testId}-recap`}
          style={{
            border: '1px solid #e2e0db', borderRadius: 12, padding: '14px 15px',
            background: '#fafaf8',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', letterSpacing: '.06em', marginBottom: 9 }}>
            {t.recap.toUpperCase()}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
            <span
              data-testid={`${testId}-offer`}
              style={{ fontSize: 14.5, fontWeight: 700, color: '#0a0a0f', minWidth: 0 }}
            >
              {offerName}
            </span>
            {/* Le montant. Avec un code appliqué, c'est le prix RÉEL de
                la première échéance qui prend la place — le prix plein
                reste visible, barré. Ces deux montants viennent du
                serveur, qui les a lus sur le price Stripe : la modale
                n'en calcule aucun. */}
            <span
              data-testid={`${testId}-amount`}
              style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.02em', color: '#0a0a0f', whiteSpace: 'nowrap' }}
            >
              {promoPrice !== null ? (
                <>
                  <s style={{ fontSize: 14, fontWeight: 500, color: '#9ca3af', marginRight: 7 }}>
                    {amount}
                    {amountSuffix}
                  </s>
                  <span data-testid={`${testId}-amount-discounted`} style={{ color: '#15803d' }}>
                    {promoPrice}
                  </span>
                </>
              ) : (
                <>
                  {amount}
                  {amountSuffix && (
                    <span style={{ fontSize: 12.5, fontWeight: 500, color: '#9ca3af' }}>{amountSuffix}</span>
                  )}
                </>
              )}
            </span>
          </div>
          {shownDetails.filter(Boolean).length > 0 && (
            <div
              data-testid={`${testId}-details`}
              style={{
                marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6,
                fontSize: 12, color: '#4b5563', lineHeight: 1.5,
              }}
            >
              {shownDetails.filter(Boolean).map((d, i) => (
                <React.Fragment key={d}>
                  {i > 0 && <span style={{ color: '#d1d5db' }}>·</span>}
                  <span>{d}</span>
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Le champ vit DANS le récapitulatif : le code change le
              montant affiché juste au-dessus, les deux doivent se lire
              d'un seul regard.

              Pas de `key` : la modale rend `null` quand elle est fermée,
              donc le champ est déjà neuf à chaque ouverture et repose son
              `initialCode` tout seul. Et pendant qu'elle est ouverte,
              c'est le composant qui gère un changement d'offre — en
              conservant le code saisi et en le revalidant, ce qu'un
              remontage effacerait. */}
          {promoPlan && (
            <PromoCodeField
              plan={promoPlan}
              billingCycle={promoBillingCycle}
              language={language}
              initialCode={promoInitialCode}
              disabled={busy}
              showPrice={false}
              testId={`${testId}-promo`}
              onApplied={(data) => {
                setPromo(data);
                if (onPromoChange) onPromoChange(data);
              }}
            />
          )}
        </div>

        {error && (
          <div
            data-testid={`${testId}-error`}
            style={{
              marginTop: 12, padding: '10px 12px', background: '#fef2f2',
              border: '1px solid #fca5a5', borderRadius: 8, fontSize: 12, color: '#991b1b',
            }}
          >
            {error}
          </div>
        )}

        <ConsentExecutionCheckbox
          id={`${testId}-consent`}
          testId={`${testId}-consent`}
          checked={consent}
          onChange={setConsent}
          disabled={busy}
          style={{ marginTop: 6 }}
        />

        <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid={`${testId}-cancel`}
            style={{
              flex: '1 1 0', minWidth: 0, minHeight: 44, padding: '11px 16px',
              background: '#ffffff', color: '#0a0a0f',
              border: '1px solid #d1d5db', borderRadius: 10,
              fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            {t.cancel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={blocked}
            data-testid={`${testId}-pay`}
            style={{
              flex: '1 1 0', minWidth: 0, minHeight: 44, padding: '11px 16px',
              background: '#1a56db', color: '#ffffff',
              border: 'none', borderRadius: 10,
              fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
              cursor: blocked ? 'not-allowed' : 'pointer',
              opacity: blocked ? 0.5 : 1,
            }}
          >
            {busy ? t.busy : t.pay}
          </button>
        </div>
      </div>
    </div>
  );
}
